import React, { useEffect, useState } from "react";

import { SupplierLogo } from "../../../types/proposalDocuments.types";
import { useProposalDocument } from "../proposalDocumentsContext";

import TermInput from "./TermInput";

interface SectionDetailsFieldsProps {
  idBase: string;
  current: boolean;
  onCurrentChange: (current: boolean) => void;
  supplier: string;
  onSupplierChange: (supplier: string) => void;
  product: string;
  onProductChange: (product: string) => void;
  contractType: string;
  onContractTypeChange: (contractType: string) => void;
  term: string | null;
  onTermChange: (term: string) => void;
  termError?: string;
  supplierLogo: string | null;
  onSupplierLogoChange: (supplierLogo: string) => void;
  badge: string | null;
  onBadgeChange: (badge: string) => void;
  // Extra fields to render at the end of the Section Details grid -
  // e.g. the section's calculated consumption/cost, which only make
  // sense once a section actually exists (not during wizard creation)
  children?: React.ReactNode;
}

// Shared between quoteSection.tsx's inline editor and the section
// creation wizard - both collect the same section-level basics
const SectionDetailsFields: React.FC<SectionDetailsFieldsProps> = ({
  idBase,
  current,
  onCurrentChange,
  supplier,
  onSupplierChange,
  product,
  onProductChange,
  contractType,
  onContractTypeChange,
  term,
  onTermChange,
  termError,
  supplierLogo,
  onSupplierLogoChange,
  badge,
  onBadgeChange,
  children,
}) => {
  const { supplierLogos, fetchSupplierLogo } = useProposalDocument();

  // Remembers which logo the fetched preview belongs to, so loading/stale
  // state can be derived during render instead of set synchronously in the effect
  const [logoPreview, setLogoPreview] = useState<{
    logoId: string;
    url: string | null;
  } | null>(null);

  useEffect(() => {
    if (!supplierLogo) return;
    let cancelled = false;

    fetchSupplierLogo(supplierLogo)
      .then((url) => {
        if (!cancelled) setLogoPreview({ logoId: supplierLogo, url });
      })
      .catch(() => {
        if (!cancelled) setLogoPreview({ logoId: supplierLogo, url: null });
      });

    return () => {
      cancelled = true;
    };
  }, [supplierLogo, fetchSupplierLogo]);

  const logoPreviewLoading = !!supplierLogo && logoPreview?.logoId !== supplierLogo;
  const logoPreviewUrl = logoPreview?.logoId === supplierLogo ? logoPreview.url : null;

  return (
    <>
      <section className="quote-section-item__subsection">
        <div className="quote-section-item__subsection-header">
          <h3>Section Details</h3>
        </div>
        <div className="quote-section-item__meter-fields">
          <div className="quote-section-item__field-group quote-section-item__current-toggle">
            <label htmlFor={`${idBase}-current`}>
              <input
                id={`${idBase}-current`}
                type="checkbox"
                checked={current}
                onChange={(e) => {
                  onCurrentChange(e.target.checked);
                }}
              />
              This is the current supplier
            </label>
          </div>

          <div className="quote-section-item__field-group">
            <label htmlFor={`${idBase}-supplier`}>Supplier</label>
            <input
              id={`${idBase}-supplier`}
              type="text"
              className="quote-section-item__field-input"
              placeholder="e.g. British Gas"
              value={supplier}
              onChange={(e) => {
                onSupplierChange(e.target.value);
              }}
              aria-label="Supplier"
            />
          </div>

          <div className="quote-section-item__field-group">
            <label htmlFor={`${idBase}-product`}>Product</label>
            <input
              id={`${idBase}-product`}
              type="text"
              className="quote-section-item__field-input"
              placeholder="e.g. Fixed Electricity"
              value={product}
              onChange={(e) => {
                onProductChange(e.target.value);
              }}
              aria-label="Product"
            />
          </div>

          <div className="quote-section-item__field-group">
            <label htmlFor={`${idBase}-contract-type`}>Contract Type</label>
            <input
              id={`${idBase}-contract-type`}
              type="text"
              className="quote-section-item__field-input"
              placeholder="e.g. Fixed"
              value={contractType}
              onChange={(e) => {
                onContractTypeChange(e.target.value);
              }}
              aria-label="Contract type"
            />
          </div>

          {/* Current/rolling suppliers have no fixed term - only ask for one on new business */}
          {!current && (
            <div className="quote-section-item__field-group">
              <label htmlFor={`${idBase}-term`}>Term</label>
              <TermInput id={`${idBase}-term`} value={term ?? ""} onChange={onTermChange} />
              {termError && <div className="invalid-feedback d-block">{termError}</div>}
            </div>
          )}

          {children}
        </div>
      </section>

      <section className="quote-section-item__subsection">
        <div className="quote-section-item__subsection-header">
          <h3>Section Branding</h3>
        </div>
        <div className="quote-section-item__meter-fields">
          <div className="quote-section-item__field-group">
            <label htmlFor={`${idBase}-supplier-logo`}>Supplier Logo</label>
            <select
              id={`${idBase}-supplier-logo`}
              className="quote-section-item__field-input"
              value={supplierLogo ?? ""}
              onChange={(e) => {
                onSupplierLogoChange(e.target.value);
              }}
              aria-label="Supplier logo"
            >
              <option value="">-None-</option>
              {supplierLogos.map((logo: SupplierLogo) => (
                <option key={logo.id} value={logo.id}>
                  {logo.name}
                </option>
              ))}
            </select>

            {supplierLogo && (
              <>
                {logoPreviewLoading && (
                  <span className="quote-section-item__logo-preview-loading">Loading preview…</span>
                )}
                {logoPreviewUrl && !logoPreviewLoading && (
                  <img
                    src={logoPreviewUrl}
                    alt="Supplier logo preview"
                    className="quote-section-item__logo-preview"
                    style={{ maxHeight: 40, marginTop: 8 }}
                  />
                )}
              </>
            )}
          </div>

          <div className="quote-section-item__field-group">
            <label htmlFor={`${idBase}-badge`}>Badge</label>
            <input
              id={`${idBase}-badge`}
              type="text"
              className="quote-section-item__field-input"
              placeholder="e.g. Best Value"
              value={badge ?? ""}
              onChange={(e) => {
                onBadgeChange(e.target.value);
              }}
              aria-label="Section badge"
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default SectionDetailsFields;
